import React, { useEffect, useState } from 'react';
import { DEFAULT_REMINDER_PREFERENCES, dataService } from '../../services/dataService';
import { ReminderPreferences } from '../../types/domain';
import {
  PushState,
  basePushState,
  detectIndonesianTimezone,
  getCurrentSubscription,
  subscribeThisDevice,
  subscriptionKeys
} from '../../lib/push';

const KINDS: { key: keyof ReminderPreferences; label: string }[] = [
  { key: 'insulin', label: 'Jadwal suntik insulin (sesuai jam di Jadwal insulin)' },
  { key: 'glucose', label: 'Cek gula darah' },
  { key: 'dailyLog', label: 'Catatan harian masih kosong' },
  { key: 'hba1c', label: 'Waktunya cek HbA1c' },
  { key: 'controlVisit', label: 'Jadwal kontrol ke dokter' }
];

const STATE_TEXT: Record<Exclude<PushState, 'on' | 'off'>, string> = {
  unconfigured: 'Notifikasi belum disiapkan di aplikasi ini. Hubungi admin.',
  unsupported: 'Browser ini tidak mendukung notifikasi. Pengingat tetap muncul di halaman utama.',
  'ios-needs-install':
    'Di iPhone/iPad, notifikasi hanya bisa aktif setelah aplikasi ditambahkan ke Layar Utama ' +
    '(Bagikan → Tambah ke Layar Utama), lalu dibuka dari sana.',
  denied:
    'Notifikasi diblokir untuk situs ini. Izinkan lewat pengaturan browser, lalu muat ulang halaman.'
};

/**
 * FR-20 settings on /profile: turn push on for this device, choose which
 * reminders arrive and when. Preferences apply to every device; the on/off
 * switch is per device, because a subscription is.
 */
const ReminderSettings: React.FC<{ userId: string }> = ({ userId }) => {
  const [state, setState] = useState<PushState | null>(null);
  const [prefs, setPrefs] = useState<ReminderPreferences>(DEFAULT_REMINDER_PREFERENCES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const blocked = basePushState();
      if (blocked) {
        if (!cancelled) setState(blocked);
        return;
      }
      try {
        const subscription = await getCurrentSubscription();
        if (!cancelled) setState(subscription ? 'on' : 'off');
      } catch {
        if (!cancelled) setState('unsupported');
      }
    })();
    dataService
      .getReminderPreferences(userId)
      .then(p => {
        if (!cancelled) setPrefs(p);
      })
      .catch(() => {
        // Defaults stay; saving will surface a real error if there is one.
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const run = async (action: () => Promise<void>, success: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (err: any) {
      setError(err?.message || 'Terjadi kesalahan.');
    } finally {
      setBusy(false);
    }
  };

  const enable = () =>
    run(async () => {
      const subscription = await subscribeThisDevice();
      await dataService.savePushSubscription(userId, subscriptionKeys(subscription));
      // Reminders fire on the family's local clock, so the zone is captured
      // the moment a device is enabled.
      const next = { ...prefs, timezone: detectIndonesianTimezone() };
      await dataService.saveReminderPreferences(userId, next);
      setPrefs(next);
      setState('on');
    }, 'Notifikasi aktif di perangkat ini.');

  const disable = () =>
    run(async () => {
      const subscription = await getCurrentSubscription();
      if (subscription) {
        await dataService.deletePushSubscription(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setState('off');
    }, 'Notifikasi dimatikan di perangkat ini.');

  const save = () =>
    run(() => dataService.saveReminderPreferences(userId, prefs), 'Pengaturan pengingat tersimpan.');

  const test = () =>
    run(() => dataService.sendTestPush(), 'Notifikasi uji dikirim. Cek perangkat ini.');

  const set = <K extends keyof ReminderPreferences>(key: K, value: ReminderPreferences[K]) =>
    setPrefs(prev => ({ ...prev, [key]: value }));

  return (
    <section className="screen-card" aria-labelledby="reminder-title">
      <div id="reminder-title" className="section-title">
        Notifikasi pengingat
      </div>
      <div className="subtitle">
        Pengingat tetap muncul di halaman utama. Notifikasi membantu mengingatkan saat aplikasi
        tidak dibuka.
      </div>

      {state === null ? (
        <div className="subtitle">Memeriksa perangkat...</div>
      ) : state === 'on' || state === 'off' ? (
        <div className="target-actions">
          {state === 'off' ? (
            <button type="button" className="btn-primary" disabled={busy} onClick={enable}>
              Aktifkan notifikasi di perangkat ini
            </button>
          ) : (
            <>
              <button type="button" className="btn-secondary" disabled={busy} onClick={test}>
                Kirim notifikasi uji
              </button>
              <button type="button" className="btn-secondary" disabled={busy} onClick={disable}>
                Matikan di perangkat ini
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="profile-nudge" role="note">
          {STATE_TEXT[state]}
        </div>
      )}

      <div className="clinical-form">
        <fieldset className="reminder-kinds">
          <legend className="subtitle">Ingatkan saya tentang</legend>
          {KINDS.map(kind => (
            <label key={kind.key} className="reminder-kind">
              <input
                type="checkbox"
                checked={prefs[kind.key] as boolean}
                disabled={busy}
                onChange={e => set(kind.key, e.target.checked as never)}
              />
              <span>{kind.label}</span>
            </label>
          ))}
        </fieldset>

        <div className="field-group">
          <div className="field">
            <label htmlFor="reminder-glucose-time">Jam pengingat cek gula darah</label>
            <input
              id="reminder-glucose-time"
              type="time"
              value={prefs.glucoseTime}
              disabled={busy || !prefs.glucose}
              onChange={e => set('glucoseTime', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="reminder-daily-time">Jam pengingat catatan harian</label>
            <input
              id="reminder-daily-time"
              type="time"
              value={prefs.dailyLogTime}
              disabled={busy || !prefs.dailyLog}
              onChange={e => set('dailyLogTime', e.target.value)}
            />
          </div>
        </div>

        {error ? <div className="target-band-error">{error}</div> : null}
        {notice ? <div className="target-band-preview">{notice}</div> : null}

        <div className="target-actions">
          <button type="button" className="btn-primary" disabled={busy} onClick={save}>
            Simpan pengaturan pengingat
          </button>
        </div>
      </div>
    </section>
  );
};

export default ReminderSettings;
